// #genai Phase 2 "Add Bank Payment Method" route (/billing-information/add-ach). Full ACH + billing
// form renders on load (Account Type lives inside AchPaymentInformation phase2). Gated by the
// ENABLE_MULTI_PAYMENT_METHODS flag.
import React, {
  useEffect, useState, useRef, useCallback,
} from 'lib/react';
import { Redirect } from 'lib/router';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import {
  EVGrid, EVTypography, EVButton, EVArrowBackIcon,
} from 'lib/components';
import { BILLING_INFORMATION_ROUTE } from 'constants/RouteConstants';
import AchPaymentInformation from 'commonComponents/paymentInformation/AchPaymentInformation.component';
import {
  PAYMENT_METHOD_OPTION_IDS,
  ACH_PAYMENT_VARIANTS,
} from 'commonComponents/paymentInformation/PaymentInformationConstants';
import TextInputField from 'commonComponents/textInputField';
import AddressInformation from 'commonComponents/addressInformation';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import ValidationMessageBanner from 'commonComponents/validationMessageBanner';
import history from 'store/CreateHistory';
import { getAchAccountTypeLabelKey } from '../SavedPaymentMethods/paymentMethods.viewModel';
import useAddPaymentRouteAccess, { ADD_PAYMENT_ROUTE_ACCESS } from '../useAddPaymentRouteAccess';
import AddPaymentMethodLoader from '../components/AddPaymentMethodLoader/AddPaymentMethodLoader.component';
import {
  updateAchPaymentInfo,
  updateAchConsent,
  updateAchDefault,
  updateAddressInfo,
  updateAddressInfoErrors,
  submitBillingInfo,
  setSelectedPaymentMethod,
  resetBillingSubmission,
  resetAddPaymentFormState,
  loadBillingAccountContext,
  setRecentlyAddedPaymentMethod,
  updateAddressValidationErrorsForDisplay,
  updateAchPaymentInfoErrors,
  updateAchPaymentInfoErrorsForBanner,
} from '../BillingInformation.actions';
import { buildElementRefInfoState } from '../BillingInformation.component';
import { showValidationBanner, updateMessages, shouldUpdateElementRefMessages } from '../BillingInformation.util';
import {
  getSubmitErrorDialogKind,
  getBillToInlineFieldError,
  getBillToInlinePaymentFieldError,
} from '../BankAccountInfoErrors';
import PaymentMethodExistsDialog from './SubmitErrorDialogs/PaymentMethodExistsDialog.component';
import SomethingWentWrongDialog from './SubmitErrorDialogs/SomethingWentWrongDialog.component';
import AddAchPaymentMethodStyles from './AddAchPaymentMethodStyles';

function AddAchPaymentMethod() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = AddAchPaymentMethodStyles();
  const achPaymentInfoRef = useRef(null);

  const routeAccess = useAddPaymentRouteAccess();
  // #genai billPayerId is normally hydrated by the /billing-information page; on a direct visit to
  // this route it is missing, so we hydrate account context below.
  const billPayerId = useSelector(({ userData }) => userData.billingInformation?.billPayerId);

  const {
    isLoading,
    achPaymentInfo,
    achPaymentInfoErrors,
    achPaymentInfoErrorsForBanner,
    achConsent,
    achInfo,
    isACHDefault,
    addressInfo,
    addressInfoErrorsForDisplay,
    addressInfoErrorsForBannerDisplay,
    submitInfo,
  } = useSelector(({ billingInformation }) => billingInformation);

  const [elementRefInfo, setElementRefInfo] = useState();

  // #genai Guards the one-time post-save redirect so it fires exactly once.
  const didNavigateRef = useRef(false);

  // #genai didSubmitRef only flips true inside handleSubmit, so the redirect effect below can't
  // bounce the user on a stale submitInfo.showMessage left over from a prior save.
  const didSubmitRef = useRef(false);
  useEffect(() => {
    dispatch(resetAddPaymentFormState());
    dispatch(setSelectedPaymentMethod(PAYMENT_METHOD_OPTION_IDS.Ach));
    dispatch(updateAchDefault(true));
  }, [dispatch]);

  // #genai Fetch account context only when billPayerId is missing (deep-link case).
  useEffect(() => {
    if (!billPayerId) {
      dispatch(loadBillingAccountContext());
    }
  }, [dispatch, billPayerId]);

  // #genai Account Type and other ACH fields share this handler so any edit marks the form dirty.
  // First / Last name Cybersource rules run on Add Payment Method, same as Company: keystrokes
  // only store the value. Inline and banner copy are revealed by the submit validation pass.
  const handleAchPaymentInfoChange = useCallback((fieldId, event) => {
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    dispatch(updateAchPaymentInfo({ [targetId]: value, isUpdated: true }));
  }, [dispatch]);

  const handleAchConsentChange = () => dispatch(updateAchConsent(!achConsent));
  const handleAddressInfoChange = (fieldId, value) => dispatch(updateAddressInfo(fieldId, value));
  const handleAddressInfoErrorChange = (fieldId, fieldErrorValue, bannerErrorValue) => dispatch(
    updateAddressInfoErrors(fieldId, fieldErrorValue, bannerErrorValue),
  );

  const updateElementRefInfo = (componentRefInfo) => {
    setElementRefInfo((prevState) => buildElementRefInfoState(prevState, componentRefInfo));
  };

  const handleCancel = () => {
    dispatch(resetBillingSubmission());
    history.push(BILLING_INFORMATION_ROUTE);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // #genai Flag this mount's submit so the redirect effect distinguishes a real success from a
    // stale submitInfo.showMessage left over from a prior save.
    didSubmitRef.current = true;
    const transientTokenData = achPaymentInfoRef.current ? await achPaymentInfoRef.current.tokenize() : {};
    // #genai isPhase2Ach scopes the Phase 2 submit saga: Phase 2 consent copy + full required-field
    // validation on the empty Add-Bank form, and the Corporate-Checking name relaxation.
    dispatch(submitBillingInfo({ ...transientTokenData, isPhase2Ach: true }));
  };

  // #genai On a successful save, redirect to /billing-information and hand off the banner payload
  // via Redux (setRecentlyAddedPaymentMethod) — history.push state is lost across the destination's
  // list/empty-state remounts. accountTypeLabel/accountSuffix come from the server save response.
  useEffect(() => {
    if (
      didSubmitRef.current
      && submitInfo.showMessage
      && !submitInfo.errorMessage
      && !isLoading
      && !didNavigateRef.current
    ) {
      didNavigateRef.current = true;
      const savedAccountType = achInfo?.accountType || achPaymentInfo.accountType;
      dispatch(setRecentlyAddedPaymentMethod({
        accountTypeLabel: t(getAchAccountTypeLabelKey(savedAccountType)),
        accountSuffix: achInfo?.accountSuffix || '',
        isDefault: isACHDefault,
      }));
      dispatch(resetBillingSubmission());
      history.push(BILLING_INFORMATION_ROUTE);
    }
  }, [submitInfo.showMessage, submitInfo.errorMessage, isLoading]);

  // #genai Route a backend BillTo.* required-field rejection onto the inline addressInfoErrors slice
  // (red label + helper text + validation-banner line) and clear submitInfo so the generic dialog
  // stays closed — behaviourally identical to a client-side required error.
  useEffect(() => {
    const inline = getBillToInlineFieldError(submitInfo, t);
    if (inline) {
      dispatch(updateAddressInfoErrors(inline.fieldId, inline.fieldError, inline.bannerError));
      dispatch(updateAddressValidationErrorsForDisplay());
      dispatch(resetBillingSubmission());
    }
  }, [submitInfo.errorType, submitInfo.errorCode]);

  // #genai First / Last name live on the achPaymentInfo slice (not addressInfo), so the address
  // routing above can't cover them. Route BillToFirstNameMissing / BillToLastNameMissing onto the
  // whole-slice payment-info error actions. Separate effect because those actions replace the whole
  // slice, so the current errors must be in the closure (the null guard makes the extra run a no-op).
  useEffect(() => {
    const inlinePayment = getBillToInlinePaymentFieldError(submitInfo, t);
    if (inlinePayment) {
      dispatch(updateAchPaymentInfoErrors({
        ...achPaymentInfoErrors,
        [inlinePayment.fieldId]: inlinePayment.fieldError,
      }));
      dispatch(updateAchPaymentInfoErrorsForBanner({
        ...achPaymentInfoErrorsForBanner,
        [inlinePayment.fieldId]: inlinePayment.bannerError,
      }));
      dispatch(resetBillingSubmission());
    }
  }, [submitInfo.errorType, submitInfo.errorCode, achPaymentInfoErrors, achPaymentInfoErrorsForBanner]);

  // #genai Wait for billing eligibility; redirect only after it is known (flag off / ineligible).
  if (routeAccess === ADD_PAYMENT_ROUTE_ACCESS.PENDING) {
    return <AddPaymentMethodLoader />;
  }

  if (routeAccess === ADD_PAYMENT_ROUTE_ACCESS.DENIED) {
    return <Redirect to={BILLING_INFORMATION_ROUTE} />;
  }

  if (shouldUpdateElementRefMessages(elementRefInfo, addressInfoErrorsForBannerDisplay, achPaymentInfoErrorsForBanner)) {
    setElementRefInfo((prevState) => updateMessages(prevState, addressInfoErrorsForBannerDisplay, achPaymentInfoErrorsForBanner));
  }

  // #genai Backend submit failures surface via a modal dialog (dismissed by clearing submitInfo);
  // client-side field validation still uses the ValidationMessageBanner above.
  const submitErrorDialog = isLoading ? null : getSubmitErrorDialogKind(submitInfo);
  const dismissSubmitError = () => dispatch(resetBillingSubmission());
  const returnToBillingList = () => {
    dispatch(resetBillingSubmission());
    history.push(BILLING_INFORMATION_ROUTE);
  };

  return (
    <>
      {!isLoading && showValidationBanner(achPaymentInfoErrorsForBanner, addressInfoErrorsForBannerDisplay) && (
        <ValidationMessageBanner
          title={t('validation_message_banner__title')}
          heading={t('validation_message_banner__error_heading')}
          elementRefInfo={elementRefInfo}
        />
      )}
      <PaymentMethodExistsDialog
        open={submitErrorDialog === 'duplicate'}
        onOk={dismissSubmitError}
        onReturn={returnToBillingList}
      />
      <SomethingWentWrongDialog
        open={submitErrorDialog === 'generic' || submitErrorDialog === 'orphan'}
        onOk={dismissSubmitError}
        supportReference={submitErrorDialog === 'orphan' ? submitInfo.supportReference : null}
      />
      <form onSubmit={handleSubmit} className={classes.formRoot}>
        <EVGrid className={classes.card}>
          {/* #genai Mobile-only sub-header (back arrow + title); hidden on desktop via CSS. */}
          <div className={classes.mobileHeader}>
            <button
              type="button"
              className={classes.mobileHeaderIconBtn}
              onClick={handleCancel}
              aria-label={t('billing_information__add_payment_method_back')}
            >
              <EVArrowBackIcon />
            </button>
            <EVTypography className={classes.mobileHeaderTitle}>
              {t('billing_information__add_payment_method')}
            </EVTypography>
          </div>

          <EVGrid className={classes.sections}>
            {/* #genai Mobile grouper (display: contents on desktop so the layout is unchanged). */}
            <div className={classes.mobilePaymentGroup}>
              <EVTypography className={classes.sectionHeader}>
                {t('billing_information__ach_payment_information_step')}
              </EVTypography>
              {/* #genai Full ACH microform including Account Type (empty until user selects). */}
              <EVGrid className={classes.subSection}>
                <AchPaymentInformation
                  // #genai Key remounts only on true success; the failure path stays a no-op so
                  // the CyberSource iframes / typed fields aren't torn down while a dialog is open.
                  key={submitInfo.showMessage && !submitInfo.errorMessage ? 'ach-submitted' : 'ach-initial'}
                  variant={ACH_PAYMENT_VARIANTS.Phase2}
                  isLoading={isLoading}
                  paymentInfo={achPaymentInfo}
                  handleChange={handleAchPaymentInfoChange}
                  achConsent={achConsent}
                  handleConsentCheckboxChange={handleAchConsentChange}
                  paymentInfoErrors={achPaymentInfoErrors}
                  ref={achPaymentInfoRef}
                  updateElementRefs={updateElementRefInfo}
                  achInfo={achInfo}
                />
              </EVGrid>
            </div>

            {/* #genai Billing group: First/Last name, Billing Address + default checkbox. */}
            <div className={classes.mobileBillingGroup}>
              <EVGrid className={classes.billingHeaderBlock}>
                <EVTypography className={classes.sectionHeader}>
                  {t('billing_information__ach_billing_information')}
                </EVTypography>
                <EVTypography className={classes.billingSubtitle}>
                  {t('billing_information__add_card_billing_information_subtitle')}
                </EVTypography>
              </EVGrid>
              {!isLoading && (
                <EVGrid className={classes.subSection}>
                  <div className={classes.nameRow}>
                    <EVGrid item>
                      <TextInputField
                        inputId="firstName"
                        inputValue={achPaymentInfo.firstName}
                        size="small"
                        handleChange={(event) => handleAchPaymentInfoChange('firstName', event)}
                        inputLabelProps={{
                          className: classes.nameLabel,
                          classes: { asterisk: classes.asterisk },
                          // #genai Always mandatory (every account type), matching Company / Billing email.
                          required: true,
                        }}
                        inputFieldProps={{
                          fullWidth: true,
                          inputProps: { maxLength: 64 },
                          classes: { root: classes.nameInputText },
                          FormHelperTextProps: { classes: { error: classes.nameInputHelperText } },
                          inputRef: elementRefInfo?.firstName?.elementRef,
                          autoComplete: 'off',
                        }}
                        labelText={t('payment_information__first_name')}
                        helperText={achPaymentInfoErrors.firstName}
                      />
                    </EVGrid>
                    <EVGrid item>
                      <TextInputField
                        inputId="lastName"
                        inputValue={achPaymentInfo.lastName}
                        size="small"
                        handleChange={(event) => handleAchPaymentInfoChange('lastName', event)}
                        inputLabelProps={{
                          className: classes.nameLabel,
                          classes: { asterisk: classes.asterisk },
                          // #genai Always mandatory (every account type), matching Company / Billing email.
                          required: true,
                        }}
                        inputFieldProps={{
                          fullWidth: true,
                          inputProps: { maxLength: 64 },
                          classes: { root: classes.nameInputText },
                          FormHelperTextProps: { classes: { error: classes.nameInputHelperText } },
                          inputRef: elementRefInfo?.lastName?.elementRef,
                          autoComplete: 'off',
                        }}
                        labelText={t('payment_information__last_name')}
                        helperText={achPaymentInfoErrors.lastName}
                      />
                    </EVGrid>
                  </div>
                </EVGrid>
              )}
              <EVGrid className={`${classes.subSection} ${classes.addressWrap}`}>
                <AddressInformation
                  isLoading={isLoading}
                  addressInfo={addressInfo}
                  handleChange={handleAddressInfoChange}
                  addressInfoErrors={addressInfoErrorsForDisplay}
                  handleErrorChange={handleAddressInfoErrorChange}
                  onCancel={handleCancel}
                  updateElementRefs={updateElementRefInfo}
                  disableCompanyCountry={false}
                  isPhase2AddPayment
                />
              </EVGrid>

              {!isLoading && (
                <EVGrid className={classes.defaultCheckbox}>
                  <LabelledCheckbox
                    checkboxId="makeDefaultAchMethod"
                    checked={isACHDefault}
                    checkboxValue={isACHDefault}
                    handleCheckboxSelectChange={() => dispatch(updateAchDefault(!isACHDefault))}
                    checkboxLabel={t('billing_information__add_ach_default_method')}
                  />
                </EVGrid>
              )}
            </div>
          </EVGrid>

          {!isLoading && (
            <EVGrid className={classes.footer}>
              <EVButton
                id="add-ach-submit-btn"
                type="submit"
                variant="contained"
                className={classes.addButton}
                // #genai Block Save until billPayerId hydrates (deep-link case) so the ACH PUT
                // never fires without it; a no-op on the normal navigation where it's already set.
                disabled={!achPaymentInfo.isUpdated || !billPayerId}
              >
                {/* #genai Same label on mobile and desktop (Figma: "Add Payment Method"). */}
                {t('billing_information__add_payment_method')}
              </EVButton>
              <EVButton
                id="add-ach-cancel-btn"
                variant="outlined"
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                {t('billing_information__add_payment_method_cancel')}
              </EVButton>
            </EVGrid>
          )}
        </EVGrid>
      </form>
    </>
  );
}

export default AddAchPaymentMethod;
